import React from 'react';

/**
 * Shared severity / category / scanner badge helpers.
 *
 * Extracted from the duplicated maps that previously lived inline in
 * FindingDetailPage.jsx and VulnerabilityItem.jsx so both render the same way,
 * and so new finding types (e.g. DAST / pentest) can plug in one place.
 */

// Pill background/text/border classes per severity.
export const SEVERITY_BADGE_CLASSES = {
  critical: 'bg-red-50 text-red-700 border border-red-200',
  high: 'bg-orange-50 text-orange-700 border border-orange-200',
  medium: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
  low: 'bg-blue-50 text-blue-700 border border-blue-200',
};

// Left-accent border (+ light bg) classes per severity, used on the card edge.
export const SEVERITY_BORDER_CLASSES = {
  critical: 'border-red-500 bg-red-50',
  high: 'border-orange-500 bg-orange-50',
  medium: 'border-yellow-500 bg-yellow-50',
  low: 'border-blue-500 bg-blue-50',
};

// Confidence pill classes (multi-agent AI findings).
export const CONFIDENCE_CLASSES = {
  high: 'bg-green-50 text-green-700 border border-green-200',
  medium: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
  low: 'bg-gray-50 text-gray-600 border border-gray-200',
};

// Category code -> short label. Replaces the old hardcoded ternary that rendered
// ANY unknown category as "SECRETS". `dynamic_analysis` is pentest / DAST.
export const CATEGORY_LABELS = {
  code_security: 'SAST',
  third_party_dependencies: 'SCA',
  secret_detection: 'SECRETS',
  dynamic_analysis: 'PENTEST',
};

/**
 * Human label for a finding's category. A DAST scanner wins over the category
 * map; otherwise fall back to the category map, then an upper-cased category,
 * never a misleading "SECRETS" default.
 */
export const getCategoryLabel = (category, scanner) => {
  if (scanner === 'dast') return 'DAST';
  if (category && CATEGORY_LABELS[category]) return CATEGORY_LABELS[category];
  return category ? String(category).replace(/_/g, ' ').toUpperCase() : null;
};

/**
 * Severity / status pill. Renders "FIXED" when status is fixed, otherwise the
 * upper-cased severity (or "UNKNOWN").
 */
// Padding per size. `sm` matches the compact pills used in VulnerabilityItem,
// `md` the larger pills used on FindingDetailPage.
const SIZE_PADDING = {
  sm: 'px-2 py-0.5',
  md: 'px-3 py-1',
};

export const SeverityBadge = ({ severity, status, size = 'md', className = '' }) => {
  const isFixed = status === 'fixed';
  const sev = severity ? String(severity).toLowerCase() : null;
  const classes = isFixed
    ? 'bg-green-50 text-green-700 border border-green-200'
    : SEVERITY_BADGE_CLASSES[sev] || SEVERITY_BADGE_CLASSES.low;
  return (
    <span className={`text-xs font-semibold rounded-full ${SIZE_PADDING[size] || SIZE_PADDING.md} ${classes} ${className}`}>
      {isFixed ? 'FIXED' : sev ? sev.toUpperCase() : 'UNKNOWN'}
    </span>
  );
};

/** Category pill (blue). Renders nothing for an unknown/empty category. */
export const CategoryBadge = ({ category, scanner, size = 'md', className = '' }) => {
  const label = getCategoryLabel(category, scanner);
  if (!label) return null;
  return (
    <span className={`text-xs font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200 uppercase ${SIZE_PADDING[size] || SIZE_PADDING.md} ${className}`}>
      {label}
    </span>
  );
};

/** Scanner pill (purple). */
export const ScannerBadge = ({ scanner, size = 'md', className = '' }) => {
  if (!scanner) return null;
  return (
    <span className={`text-xs font-semibold rounded-full bg-purple-50 text-purple-700 border border-purple-200 uppercase ${SIZE_PADDING[size] || SIZE_PADDING.md} ${className}`}>
      {scanner}
    </span>
  );
};

export default SeverityBadge;
